export const GetPrintLayout = (printType) => {
  switch (printType) {
    case 1:
      return { columns: 2, rows: 1 };
    case 2:
      return { columns: 2, rows: 1 };
    case 3:
      return { columns: 2, rows: 1 };
    case 4:
      return { columns: 2, rows: 1 };
    case 5:
      return { columns: 2, rows: 1 };
    case 6:
      return { columns: 2, rows: 1 };
    case 7:
      return { columns: 2, rows: 1 };
    default:
      return { columns: 2, rows: 1 };
  }
};

export function downloadBlobFromResponse(
  response,
  fallbackFileName = "download.xlsx",
) {
  const blob = new Blob([response.data]);

  let fileName = fallbackFileName;
  const disposition = response.headers["content-disposition"];
  if (disposition && disposition.includes("filename=")) {
    const match = disposition.match(/filename="?(.+?)"?$/);
    if (match?.[1]) {
      fileName = decodeURIComponent(match[1]);
    }
  }

  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Open a PDF blob in a new browser tab so the user can review it before
// printing/downloading. Preferred over printPdfBlob for workflow auto-print
// (JV submit/approve/post), where the user wants to *see* the receipt rather
// than be ambushed by the OS print dialog. Uses a synthetic anchor click
// (more reliable than window.open through pop-up blockers when invoked from
// async callbacks). URL is revoked after 60s.
export const openPdfBlob = (blobData) => {
  const blob = new Blob([blobData], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// Stream a PDF blob into a hidden iframe and trigger the browser's print dialog.
// Used wherever we want "auto-print after action" — e.g. JV save handlers and the
// per-row Print menu. Falls back to opening the PDF in a new tab if iframe.print()
// is blocked. The iframe is removed after 60s so it doesn't pile up DOM nodes.
export const printPdfBlob = (blobData) => {
  const blob = new Blob([blobData], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.src = url;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch {
      window.open(url, "_blank");
    }
    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(url);
    }, 60000);
  };
};

export const DiscountAdjustment = (originalAmt, discountAmt, returnAmt) => {
  const discountPercentage = discountAmt / originalAmt;
  const discountOnReturn = returnAmt * discountPercentage;
  const refundAmt = returnAmt - discountOnReturn;

  return { discountOnReturn, refundAmt };
};

export const PrintDiv = (printDivId) => {
  const printEl = document.getElementById(printDivId);
  const printContents = printEl.innerHTML;

  // Read print styles from data attribute if present
  const layoutEl = printEl.querySelector("[data-print-styles]");
  const printMeta = layoutEl
    ? JSON.parse(layoutEl.getAttribute("data-print-styles"))
    : {};

  const pageRule = printMeta.page || "size: A4; margin: 0;";
  const fontFamily =
    printMeta.fontFamily || "'Arial Narrow', 'Helvetica Neue', Arial, sans-serif";
  const rollWidth = printMeta.rollWidth || "100%";

  const frame = document.createElement("iframe");
  frame.name = "printFrame";
  frame.style.position = "absolute";
  frame.style.top = "-1000000px";
  document.body.appendChild(frame);

  const frameDoc = frame.contentWindow
    ? frame.contentWindow
    : frame.contentDocument.document
      ? frame.contentDocument.document
      : frame.contentDocument;

  frameDoc.document.open();
  frameDoc.document.write(
    `<html><head><title></title>
    <style>
      @page { ${pageRule} }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      html, body {
        margin: 0; padding: 0;
        width: ${rollWidth};
        font-family: ${fontFamily};
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        color-adjust: exact;
      }
      @media print {
        img, svg {
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
        }
      }
    </style>
    </head><body>${printContents}</body></html>`
  );
  frameDoc.document.close();

  const triggerPrint = () => {
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } finally {
      document.body.removeChild(frame);
    }
  };

  // Wait for fonts AND images before printing; cap the wait so a slow/broken
  // image can never block the print dialog indefinitely. Logos are remote URLs
  // re-fetched inside the iframe, so on a live server they are often still
  // in-flight when the dialog opens — waiting on them fixes the missing-logo race.
  const idoc = frame.contentDocument || frameDoc.document;

  const fontsReady = idoc?.fonts?.ready ? idoc.fonts.ready : Promise.resolve();

  const imagesReady = (() => {
    const imgs = Array.from(idoc?.images || []);
    if (!imgs.length) return Promise.resolve();
    return Promise.all(
      imgs.map((img) =>
        img.complete && img.naturalWidth > 0
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.addEventListener("load", resolve, { once: true });
              img.addEventListener("error", resolve, { once: true }); // don't hang on a bad URL
            })
      )
    );
  })();

  const safetyCap = new Promise((resolve) => setTimeout(resolve, 3000));

  Promise.race([Promise.all([fontsReady, imagesReady]), safetyCap]).then(() => {
    setTimeout(triggerPrint, 50);
  });

  return false;
};

export const RandomCode = (length = 10) => {
  if (typeof length !== "number" || length <= 0) {
    throw new Error("Length must be a positive number");
  }

  const maxRandomValue = Math.pow(10, length) - 1;
  const randomNum = Math.floor(Math.random() * maxRandomValue);

  return randomNum;
};

export const MaxLength = (val, length = 5) => {
  return val && val.replace(/[^0-9.]/g, "").slice(0, length);
};

export const handleValidationErrors = (res, setErrors) => {
  if (res?.status === 400 && res?.errors) {
    setErrors(
      Object.fromEntries(
        Object.entries(res.errors).map(([key, msgs]) => [key, msgs[0]])
      )
    );
    return true;
  }
  return false;
};

export const playAudio = (src, volume = 1.0) => {
  try {
    const audio = new Audio(src);
    audio.volume = volume;
    audio.play().catch((error) => {
      console.error("Audio playback failed:", error);
    });
  } catch (err) {
    console.error("Error initializing audio:", err);
  }
};
